import { useEffect, useState } from "react";
import CustomAnswerModal from "../../components/ui/OtherModal/CustomAnswerModal";
import { specifyTargetKey } from "./core";
import type { SpecifyControlBinding, SpecifyFieldDefinition } from "./types";

const initialTargets = <Event,>(
  fields: readonly SpecifyFieldDefinition<Event>[],
  active: boolean,
  error?: string,
) =>
  active
    ? fields
        .filter((field) => !field.disabled && field.selected && Boolean(error))
        .map((field) => specifyTargetKey(field.target))
    : [];

type SpecifyControlProps<Event> = {
  attemptId: number;
  active: boolean;
  error?: string;
  fields: readonly SpecifyFieldDefinition<Event>[];
  canSubmit: boolean;
  retainedValues: Readonly<Record<string, string>>;
  retain: (key: string, value: string | undefined) => void;
  apply: (event: Event, submit: boolean) => void;
};

const canRemove = <Event,>(field: SpecifyFieldDefinition<Event>) =>
  field.remove !== undefined && field.selected;

export const useSpecifyControl = <Event,>(
  props: SpecifyControlProps<Event>,
) => {
  const {
    attemptId,
    active,
    error,
    fields,
    canSubmit,
    retainedValues,
    retain,
    apply,
  } = props;
  const [editor, setEditor] = useState(() => ({
    attemptId,
    targets: initialTargets(fields, active, error),
  }));
  const targets =
    editor.attemptId === attemptId
      ? editor.targets
      : initialTargets(fields, active, error);

  useEffect(() => {
    setEditor((current) =>
      current.attemptId === attemptId
        ? current
        : { attemptId, targets: initialTargets(fields, active, error) },
    );
  }, [active, attemptId, error, fields]);

  const field = fields.find(
    (item) => specifyTargetKey(item.target) === targets[0],
  );
  const key = targets[0];
  const value = retainedValues[key] ?? field?.value ?? "";
  const finish = () => {
    retain(key!, undefined);
    setEditor({ attemptId, targets: targets.slice(1) });
  };
  const remove = () => {
    if (!canSubmit || field!.disabled) return;
    apply(field!.remove!, false);
    finish();
  };
  const control: SpecifyControlBinding = {
    fields,
    open: (target) => {
      const targetKey = specifyTargetKey(target);
      if (
        canSubmit &&
        fields.some(
          (item) =>
            specifyTargetKey(item.target) === targetKey && !item.disabled,
        )
      )
        setEditor({ attemptId, targets: [targetKey] });
    },
  };

  return {
    control,
    editor: field ? (
      <CustomAnswerModal
        open
        prompt={field.prompt}
        value={value}
        error={error}
        submitDisabled={!canSubmit || field.disabled}
        onChange={(text) => retain(key!, text)}
        onClose={() => setEditor({ attemptId, targets: [] })}
        onSubmit={() => {
          if (!canSubmit || field.disabled || !value.trim()) return;
          apply(field.confirm(value), Boolean(field.submitOnConfirm));
          finish();
        }}
        onRemove={canRemove(field) ? remove : undefined}
      />
    ) : null,
  };
};
